import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search, ArrowRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { documentTypes, documentCategories, type DocCategory } from "@/data/documentTypes";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/document-types")({
  head: () => ({
    meta: [
      { title: "80+ Document Types We Analyze | legaldecoder" },
      {
        name: "description",
        content:
          "Browse 80+ contract and document types — leases, NDAs, employment contracts, term sheets and more. Get instant AI analysis in plain English.",
      },
      { property: "og:title", content: "80+ Document Types We Analyze | legaldecoder" },
      {
        property: "og:description",
        content:
          "From lease agreements to employment contracts — we decode them all in plain English.",
      },
      { property: "og:url", content: "https://legaldecoder.io/document-types" },
    ],
    links: [{ rel: "canonical", href: "https://legaldecoder.io/document-types" }],
  }),
  component: DocumentTypesPage,
});

type Tab = "ALL" | DocCategory;
const tabs: Tab[] = ["ALL", ...documentCategories];

function DocumentTypesPage() {
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState<Tab>("ALL");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return documentTypes.filter((d) => {
      const matchesTab = tab === "ALL" || d.category === tab;
      const matchesQuery =
        !q ||
        d.name.toLowerCase().includes(q) ||
        d.shortDesc.toLowerCase().includes(q) ||
        d.category.toLowerCase().includes(q);
      return matchesTab && matchesQuery;
    });
  }, [query, tab]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="bg-gradient-to-b from-[#EEF2FF] to-white py-16">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <h1 className="text-[36px] font-bold tracking-tight text-[#111827]">
              80+ Document Types We Analyze
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base text-[#6B7280]">
              From lease agreements to employment contracts — we decode them all.
            </p>
            <div className="relative mx-auto mt-8 w-full max-w-[480px]">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9CA3AF]" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search document types..."
                className="w-full rounded-xl border border-[#E5E7EB] bg-white py-3 pl-11 pr-4 text-sm text-[#111827] placeholder:text-[#9CA3AF] focus-visible:border-[#1B3A6B]"
              />
            </div>
          </div>
        </section>

        <section className="border-b border-[#E5E7EB] bg-white">
          <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 sm:px-6 lg:justify-center lg:px-8">
            {tabs.map((t) => {
              const active = tab === t;
              return (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={cn(
                    "shrink-0 border-b-2 px-4 py-3 text-sm font-medium transition-colors",
                    active
                      ? "border-[#1B3A6B] text-[#1B3A6B]"
                      : "border-transparent text-[#6B7280] hover:text-[#111827]",
                  )}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </section>

        <section className="py-12">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {filtered.length === 0 ? (
              <p className="py-12 text-center text-sm text-[#6B7280]">
                No documents match "{query}". Try another search.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {filtered.map((doc) => (
                  <Link
                    key={doc.slug}
                    to="/document-types/$slug"
                    params={{ slug: doc.slug }}
                    className="group flex flex-col rounded-xl border border-[#E5E7EB] bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#1B3A6B] hover:shadow-md"
                  >
                    <div className="text-[32px] leading-none">{doc.emoji}</div>
                    <h3 className="mt-3 text-[14px] font-bold text-[#111827]">{doc.name}</h3>
                    <p className="mt-1 flex-1 text-[12px] leading-relaxed text-[#6B7280]">
                      {doc.shortDesc}
                    </p>
                    <span className="mt-3 inline-flex items-center gap-1 text-[13px] font-medium text-[#1B3A6B]">
                      Analyze Now{" "}
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </Link>
                ))}
              </div>
            )}
            <p className="mt-10 text-center text-xs text-[#9CA3AF]">
              NOT LEGAL ADVICE. FOR INFORMATIONAL PURPOSES ONLY.
            </p>
          </div>
        </section>
        <Footer />
      </main>
    </div>
  );
}
